// BackendPageForHIW.js — the "How It Works" page (the W4U story).
//
// Reads its data from <ConfigContext> (App.js fetches the WHOLE website dict
// once at first load — navigating here makes no backend call). Content lives in
// website_items_dev/2_how_it_works/how_it_works.yaml — edit + refresh, no rebuild.
//
// Sections rendered (the campaign's narrative arc):
//   1. Hero       — "Stop working for the tool"
//   2. Pains      — the 5 daily pains (numbered cards) + structural punch line
//   3. ThirdWay   — two bad options → the third-way reveal + legacy-way quote
//   4. Tailored   — customized vs tailored (suit metaphor) + honest test line
//   5. Method     — the 5-step W4U process + before/after line
//   6. Results    — the 4 tangible results + tagline
//   7. FitCheck   — honest fit test (5 when→because rows) + closing
//   8. Cta        — closer + button to /contact

import React, { useContext } from 'react';
import { ConfigContext } from '../../App';
import { OrangeButton } from '../../myReactLib/BasicItems';
import {
  Navigation,
  Footer,
  PlaceholderPage,
  getText,
  getStyle,
  getBottomMargin,
  RT,
} from '../../myReactLib/MainBlocks';

function BackendPageForHIW() {
  const { config, homepageData, howItWorksData, contactData } = useContext(ConfigContext);

  if (!homepageData) return null;
  const menuBar = homepageData.menu_bar;
  const prefix  = config.PREFIX;
  const data    = howItWorksData;

  // released mode (webserver -W4U flag): the page is still being worked on → show the
  // "coming soon" placeholder instead of the real content (flag set by the backend loader)
  if (data && data.show_placeholder) {
    return (
      <PlaceholderPage
        title={getText(menuBar.page_1_title) || 'How It Works'}
        menuBar={menuBar}
        prefix={prefix}
        config={config}
        contactData={contactData}
        footer={homepageData.footer}
      />
    );
  }

  return (
    <div className="w4u-page">
      <Navigation menuBar={menuBar} prefix={prefix} />
      <main className="w4u-main">
        {!data && <div className="w4u-loading">Loading…</div>}
        {data && (
          <>
            <HiwHero     hero={data.hero} />
            <HiwPains    pains={data.pains} />
            <HiwThirdWay thirdWay={data.third_way} />
            <HiwTailored tailored={data.tailored} />
            <HiwMethod   method={data.method} />
            <HiwResults  results={data.results} />
            <HiwFitCheck fitCheck={data.fit_check} />
            <HiwCta      cta={data.cta} />
          </>
        )}
      </main>
      <Footer menuBar={menuBar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

// ----- 1. Hero -----

const HiwHero = ({ hero }) => {
  if (!hero) return null;
  return (
    <section className="w4u-hiw__hero" style={getBottomMargin(hero.bottom_margin)}>
      <h1 className="w4u-hiw__hero-title" style={getStyle(hero.title)}>{RT(hero.title)}</h1>
      <p className="w4u-hiw__hero-subtitle" style={getStyle(hero.subtitle)}>{RT(hero.subtitle)}</p>
    </section>
  );
};

// ----- 2. Pains -----

const HiwPains = ({ pains }) => {
  if (!pains) return null;
  const items = Object.values(pains.items || {});
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(pains.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(pains.heading)}>{RT(pains.heading)}</h2>
      {pains.intro && <p className="w4u-hiw__intro" style={getStyle(pains.intro)}>{RT(pains.intro)}</p>}
      <div className="w4u-hiw__pain-grid">
        {items.map((item, i) => (
          <article key={i} className="w4u-hiw__pain-card">
            <span className="w4u-hiw__pain-number" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
            <h3 className="w4u-hiw__card-title" style={getStyle(item.title)}>{RT(item.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(item.body)}>{RT(item.body)}</p>
          </article>
        ))}
      </div>
      {pains.punch_line && (
        <p className="w4u-hiw__punch" style={getStyle(pains.punch_line)}>{RT(pains.punch_line)}</p>
      )}
    </section>
  );
};

// ----- 3. Third way -----

const HiwThirdWay = ({ thirdWay }) => {
  if (!thirdWay) return null;
  const options = Object.values(thirdWay.options || {});
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(thirdWay.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(thirdWay.heading)}>{RT(thirdWay.heading)}</h2>
      <div className="w4u-hiw__options">
        {options.map((opt, i) => (
          <article key={i} className="w4u-hiw__option-card">
            <h3 className="w4u-hiw__card-title" style={getStyle(opt.title)}>{RT(opt.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(opt.body)}>{RT(opt.body)}</p>
          </article>
        ))}
      </div>
      <div className="w4u-hiw__reveal">
        <h3 className="w4u-hiw__reveal-title" style={getStyle(thirdWay.reveal_title)}>{RT(thirdWay.reveal_title)}</h3>
        <p className="w4u-hiw__reveal-body" style={getStyle(thirdWay.reveal_body)}>{RT(thirdWay.reveal_body)}</p>
      </div>
      {thirdWay.legacy_quote && (
        <blockquote className="w4u-hiw__quote" style={getStyle(thirdWay.legacy_quote)}>{RT(thirdWay.legacy_quote)}</blockquote>
      )}
    </section>
  );
};

// ----- 4. Tailored -----

const HiwTailored = ({ tailored }) => {
  if (!tailored) return null;
  const compare = tailored.compare || {};
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(tailored.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(tailored.heading)}>{RT(tailored.heading)}</h2>
      {tailored.intro && <p className="w4u-hiw__prose" style={getStyle(tailored.intro)}>{RT(tailored.intro)}</p>}
      <div className="w4u-hiw__compare">
        {compare.customized && (
          <article className="w4u-hiw__compare-card w4u-hiw__compare-card--customized">
            <h3 className="w4u-hiw__card-title" style={getStyle(compare.customized.title)}>{RT(compare.customized.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(compare.customized.body)}>{RT(compare.customized.body)}</p>
          </article>
        )}
        {compare.tailored && (
          <article className="w4u-hiw__compare-card w4u-hiw__compare-card--tailored">
            <h3 className="w4u-hiw__card-title" style={getStyle(compare.tailored.title)}>{RT(compare.tailored.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(compare.tailored.body)}>{RT(compare.tailored.body)}</p>
          </article>
        )}
      </div>
      {tailored.punch_line && (
        <p className="w4u-hiw__punch" style={getStyle(tailored.punch_line)}>{RT(tailored.punch_line)}</p>
      )}
      {tailored.test_line && (
        <p className="w4u-hiw__test-line" style={getStyle(tailored.test_line)}>{RT(tailored.test_line)}</p>
      )}
    </section>
  );
};

// ----- 5. Method -----

const HiwMethod = ({ method }) => {
  if (!method) return null;
  const steps = Object.values(method.steps || {});
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(method.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(method.heading)}>{RT(method.heading)}</h2>
      {method.intro && <p className="w4u-hiw__intro" style={getStyle(method.intro)}>{RT(method.intro)}</p>}
      <ol className="w4u-hiw__steps">
        {steps.map((step, i) => (
          <li key={i} className="w4u-hiw__step">
            <span className="w4u-hiw__step-number" aria-hidden="true">{i + 1}</span>
            <h3 className="w4u-hiw__card-title" style={getStyle(step.title)}>{RT(step.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(step.body)}>{RT(step.body)}</p>
          </li>
        ))}
      </ol>
      {method.before_after && (
        <p className="w4u-hiw__punch" style={getStyle(method.before_after)}>{RT(method.before_after)}</p>
      )}
    </section>
  );
};

// ----- 6. Results -----

const HiwResults = ({ results }) => {
  if (!results) return null;
  const items = Object.values(results.items || {});
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(results.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(results.heading)}>{RT(results.heading)}</h2>
      <div className="w4u-hiw__results-grid">
        {items.map((item, i) => (
          <article key={i} className="w4u-hiw__result-card">
            <h3 className="w4u-hiw__card-title" style={getStyle(item.title)}>{RT(item.title)}</h3>
            <p className="w4u-hiw__card-body" style={getStyle(item.body)}>{RT(item.body)}</p>
          </article>
        ))}
      </div>
      {results.tagline && (
        <p className="w4u-hiw__punch" style={getStyle(results.tagline)}>{RT(results.tagline)}</p>
      )}
    </section>
  );
};

// ----- 7. Fit check -----

const HiwFitCheck = ({ fitCheck }) => {
  if (!fitCheck) return null;
  const signals = Object.values(fitCheck.signals || {});
  return (
    <section className="w4u-hiw__section" style={getBottomMargin(fitCheck.bottom_margin)}>
      <h2 className="w4u-hiw__heading" style={getStyle(fitCheck.heading)}>{RT(fitCheck.heading)}</h2>
      {fitCheck.intro && <p className="w4u-hiw__prose" style={getStyle(fitCheck.intro)}>{RT(fitCheck.intro)}</p>}
      <div className="w4u-hiw__signals">
        {signals.map((sig, i) => (
          <div key={i} className="w4u-hiw__signal">
            <p className="w4u-hiw__signal-when" style={getStyle(sig.when)}>{RT(sig.when)}</p>
            <p className="w4u-hiw__signal-because" style={getStyle(sig.because)}>{RT(sig.because)}</p>
          </div>
        ))}
      </div>
      {fitCheck.closing && (
        <p className="w4u-hiw__punch" style={getStyle(fitCheck.closing)}>{RT(fitCheck.closing)}</p>
      )}
    </section>
  );
};

// ----- 8. CTA -----

const HiwCta = ({ cta }) => {
  if (!cta) return null;
  return (
    <section className="w4u-hiw__cta" style={getBottomMargin(cta.bottom_margin)}>
      {cta.setup_line_1 && <p className="w4u-hiw__cta-line" style={getStyle(cta.setup_line_1)}>{RT(cta.setup_line_1)}</p>}
      {cta.setup_line_2 && <p className="w4u-hiw__cta-line" style={getStyle(cta.setup_line_2)}>{RT(cta.setup_line_2)}</p>}
      {cta.punch_line && <p className="w4u-hiw__cta-punch" style={getStyle(cta.punch_line)}>{RT(cta.punch_line)}</p>}
      <OrangeButton to="/contact" variant="hero" style={getStyle(cta.button_text)}>{getText(cta.button_text) ? RT(cta.button_text) : 'Contact us'}</OrangeButton>
    </section>
  );
};

export default BackendPageForHIW;
